import React from "react";
import MapComponent from "./MapComponent";
import ScheduleMeetButton from "./ScheduleMeetButton";
import { COLORS } from '../constants';
import { formatDateToUS } from '../utils/dateFormatter';

// Calendar Icon SVG
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// Clock Icon SVG
const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// Location Icon SVG
const LocationIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

// User Icon SVG
const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const NotificationCard = ({ note, senders, user, respondingId, setRespondingId, response, setResponse, handleAccept, handleReject, handleSuggest, canRespond }) => {
  const mapCenter = { lat: 40.51076, lng: -88.99346 };
  
  return (
    <div style={{
      marginBottom: '16px',
      padding: 0,
      border: '1px solid #e8e8e8',
      borderRadius: '16px',
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
      minHeight: '200px',
      maxWidth: '800px',
      width: '100%',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
      transition: 'all 0.2s ease',
      ':hover': {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      }
    }}>
      {/* Details on the left */}
      <div style={{
        flex: 1,
        minWidth: 0,
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '16px',
      }}>
        {/* Header */}
        <div>
          <div style={{
            fontSize: '18px',
            fontWeight: '700',
            color: COLORS.secondary,
            marginBottom: '12px',
            letterSpacing: '-0.3px'
          }}>
            {note.title}
          </div>
          
          {/* Sender Info */}
          {note.senderId && senders[note.senderId] && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: '#666',
              marginBottom: '16px'
            }}>
              <UserIcon />
              <span>From <strong style={{ color: COLORS.secondary }}>{senders[note.senderId].name || senders[note.senderId].email}</strong></span>
            </div>
          )}
          
          {/* Meeting Details */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            padding: '16px',
            backgroundColor: '#fafbfc',
            borderRadius: '12px',
            border: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#444' }}>
              <CalendarIcon />
              <span><strong>Date:</strong> {formatDateToUS(note.responseDate || note.date)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#444' }}>
              <ClockIcon />
              <span><strong>Time:</strong> {note.responseTime || note.time}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', color: '#444' }}>
              <div style={{ marginTop: '2px' }}><LocationIcon /></div>
              <span style={{ flex: 1, wordBreak: 'break-word' }}>
                <strong>Location:</strong> {note.location?.address || 'No location specified'}
              </span>
            </div>
          </div>
          
          {/* Status Badge */}
          {note.status && (
            <div style={{
              display: 'inline-block',
              marginTop: '12px',
              padding: '6px 14px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '600',
              backgroundColor: 
                note.status === 'accepted' ? '#e8f5e9' :
                note.status === 'rejected' ? '#ffebee' :
                '#fff3e0',
              color:
                note.status === 'accepted' ? '#2e7d32' :
                note.status === 'rejected' ? '#c62828' :
                '#ef6c00'
            }}>
              {/* Show "Awaiting Response" if pending and you can't respond (you're waiting) */}
              {note.status === 'pending' && !canRespond && note.status !== 'accepted' && note.status !== 'rejected'
                ? 'Awaiting Response'
                : note.status.charAt(0).toUpperCase() + note.status.slice(1)}
            </div>
          )}
        </div>
        
        {/* Action Buttons */}
        {canRespond && respondingId === null && user.uid !== note.lastActionBy && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
            }}>
              <button
                style={{
                  flex: 1,
                  minWidth: '120px',
                  background: COLORS.primary,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px 20px',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(206, 17, 38, 0.2)'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#a00e1f'}
                onMouseOut={(e) => e.target.style.backgroundColor = COLORS.primary}
                onClick={() => handleAccept(note)}
              >
                Accept
              </button>
              <button
                style={{
                  flex: 1,
                  minWidth: '120px',
                  background: '#f5f5f5',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  padding: '12px 20px',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#ffe5e5';
                  e.target.style.borderColor = COLORS.primary;
                  e.target.style.color = COLORS.primary;
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#f5f5f5';
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.color = '#666';
                }}
                onClick={() => handleReject(note)}
              >
                Reject
              </button>
            </div>
            <button
              style={{
                width: '100%',
                background: '#ffffff',
                color: COLORS.primary,
                border: `2px solid ${COLORS.primary}`,
                borderRadius: '10px',
                padding: '12px 20px',
                fontWeight: '600',
                fontSize: '15px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#fef5f6';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#ffffff';
              }}
              onClick={() => setRespondingId(note.id)}
            >
              Suggest New Time
            </button>
          </div>
        )}
        
        {/* Suggest new time form */}
        {canRespond && respondingId === note.id && (
          <ScheduleMeetButton
            recipientId={note.senderId === user.uid ? note.recipientId : note.senderId}
            label="Suggest New Time"
            onSubmit={(data) => {
              // Pass all data including location to handleSuggest
              handleSuggest({
                date: data.date,
                time: data.time,
                address: data.address,
                lat: data.lat,
                lng: data.lng
              });
              setRespondingId(null);
            }}
            initialDate={note.responseDate || note.date}
            initialTime={note.responseTime || note.time}
            initialLocation={note.location}
            style={{ marginTop: '0px' }}
            open={true}
            onCancel={() => setRespondingId(null)}
            skipNotification={true}
          />
        )}
        
        {/* Timestamp */}
        <div style={{ fontSize: '12px', color: '#999', textAlign: 'right' }}>
          {note.createdAt && note.createdAt.toDate ? note.createdAt.toDate().toLocaleString() : ''}
        </div>
      </div>
      
      {/* Map on the right */}
      <div style={{
        width: '280px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 20px',
        backgroundColor: '#fafbfc',
      }}>
        <div style={{
          borderRadius: '16px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: '2px solid #ffffff',
          background: '#fff',
          padding: '8px',
          width: '100%',
          height: '220px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <MapComponent
            center={note.location?.lat && note.location?.lng ? { lat: note.location.lat, lng: note.location.lng } : mapCenter}
            marker={note.location?.lat && note.location?.lng ? { lat: note.location.lat, lng: note.location.lng } : undefined}
            width="100%"
            height="100%"
            zoom={16}
            style={{ borderRadius: '12px', maxWidth: '100%', maxHeight: '100%' }}
          />
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;